import { PendingBrands } from "../components/Brands";
import { PendingFees } from "../components/MerchantFees";
import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { FileSpreadsheet, UploadCloud, Save, CheckCircle2 } from "lucide-react";
import { api, errMsg } from "../lib/api";
import { money, monthLabel } from "../lib/format";
import { PageHeader, Panel } from "../components/Widgets";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { ColumnPicker } from "../components/ColumnPicker";
import { BulkUpload } from "../components/BulkUpload";

const FIELDS = [
  { key: "month", label: "Report month (date)" },
  { key: "merchant_id", label: "Merchant number", required: true },
  { key: "merchant_name", label: "Merchant name" },
  { key: "agent", label: "Agent sales code", required: true },
  { key: "open_date", label: "Open date (counts new accounts)" },
  { key: "residual", label: "Residual", required: true },
  { key: "mastercard", label: "Mastercard volume" },
  { key: "visa", label: "Visa volume" },
  { key: "discover", label: "Discover volume" },
  { key: "amex", label: "Amex volume" },
  { key: "debit", label: "Debit volume (combined)", multi: true },
  { key: "volume", label: "Total card volume (if no card breakdown)" },
  { key: "transactions", label: "Transaction count" },
];
const SIG_FIELDS = ["merchant_id", "agent", "residual"];
const thisMonth = () => new Date().toISOString().slice(0, 7);

export default function UploadReport() {
  const nav = useNavigate();
  const inputRef = useRef();
  const [file, setFile] = useState(null);
  const [headerRow, setHeaderRow] = useState(1);
  const [preview, setPreview] = useState(null);
  const [mapping, setMapping] = useState({});
  const [month, setMonth] = useState(thisMonth());
  const [source, setSource] = useState("");
  const [presets, setPresets] = useState([]);
  const [presetName, setPresetName] = useState("");
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => { api.get("/presets").then((r) => setPresets(r.data)); }, []);

  const loadPreview = async (f, hr, keepMapping) => {
    const fd = new FormData();
    fd.append("file", f);
    if (hr) fd.append("data_start_row", hr);
    try {
      const { data } = await api.post("/uploads/preview", fd);
      setPreview(data);
      setHeaderRow(data.data_start_row);
      if (data.detected_month) setMonth(data.detected_month);
      if (data.matched_preset) {
        setPresetName(data.matched_preset); setSource((s) => s || data.matched_preset);
        toast.success(`Recognised "${data.matched_preset}" format`);
      }
      setMapping((m) => (keepMapping && Object.keys(m).length ? m : data.suggested_mapping));
    } catch (e) { toast.error(errMsg(e)); setPreview(null); }
  };

  const onFile = (f) => { if (!f) return; setFile(f); setResult(null); loadPreview(f, null, false); };

  const applyPreset = (id) => {
    const p = presets.find((x) => x.id === id);
    if (!p) return;
    setPresetName(p.name); setSource((s) => s || p.name); setMapping(p.mapping);
    if (p.data_start_row !== headerRow) { setHeaderRow(p.data_start_row); if (file) loadPreview(file, p.data_start_row, true); }
    toast.success(`Applied "${p.name}" mapping`);
  };

  const savePreset = async () => {
    if (!presetName.trim()) return toast.error("Name the preset first");
    try {
      const tops = Object.fromEntries(preview.columns.map((c) => [c.letter, c.top]));
      const signature = Object.fromEntries(SIG_FIELDS.filter((f) => mapping[f] && tops[mapping[f]]).map((f) => [mapping[f], tops[mapping[f]]]));
      const { data } = await api.post("/presets", { name: presetName, data_start_row: headerRow, mapping, signature });
      setPresets((ps) => [...ps.filter((p) => p.id !== data.id), data]);
      toast.success("Column mapping saved");
    } catch (e) { toast.error(errMsg(e)); }
  };

  const ingest = async (replace = false) => {
    setBusy(true);
    const fd = new FormData();
    fd.append("file", file); fd.append("month", month); fd.append("mapping", JSON.stringify(mapping));
    fd.append("source", source); fd.append("data_start_row", headerRow); fd.append("replace", replace);
    try {
      const { data } = await api.post("/uploads/ingest", fd);
      setResult(data); toast.success(`Imported ${data.row_count} rows`);
      setTimeout(() => document.querySelector('[data-testid="ingest-result"]')?.scrollIntoView({ behavior: "smooth" }), 100);
    } catch (e) {
      if (e.response?.status === 409 && window.confirm(`${errMsg(e)}. Replace the previous import?`)) return ingest(true);
      toast.error(errMsg(e));
    } finally { setBusy(false); }
  };

  const missing = FIELDS.filter((f) => f.required && !mapping[f.key]);

  return (
    <div>
      <PageHeader eyebrow="Ingest" title="Upload monthly report" />
      <div className="grid lg:grid-cols-[1fr_380px] gap-6">
        <div className="space-y-6 min-w-0">
          <div data-testid="excel-upload-dropzone" onClick={() => inputRef.current.click()}
            onDragOver={(e) => e.preventDefault()} onDrop={(e) => { e.preventDefault(); onFile(e.dataTransfer.files[0]); }}
            className="fade-up border-2 border-dashed border-blue-500/40 bg-blue-500/5 hover:border-blue-400 hover:bg-blue-500/10 transition-colors cursor-pointer p-10 rounded-lg text-center">
            <input ref={inputRef} data-testid="excel-file-input" type="file" accept=".xlsx,.xls,.csv" className="hidden" onChange={(e) => onFile(e.target.files[0])} />
            {file ? <FileSpreadsheet className="mx-auto text-emerald-400" size={32} /> : <UploadCloud className="mx-auto text-blue-400" size={32} />}
            <div className="mt-3 font-medium" data-testid="selected-file-name">{file ? file.name : "Drop an Excel or CSV file, or click to browse"}</div>
            <div className="text-xs text-slate-500 mt-1 font-mono">{preview ? `${preview.row_count} rows · ${preview.columns.length} columns` : ".xlsx · .xls · .csv"}</div>
          </div>
          {preview && <PreviewTable preview={preview} mapping={mapping} />}
        </div>

        <div className="space-y-6">
          <Panel title="1 · Column mapping">
            <div className="p-5 space-y-4">
              {presets.length > 0 && (
                <Select onValueChange={applyPreset}>
                  <SelectTrigger data-testid="mapping-preset-select" className="bg-[#0B0F17]"><SelectValue placeholder="Load a saved mapping…" /></SelectTrigger>
                  <SelectContent>{presets.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent>
                </Select>
              )}
              <div className="flex items-center justify-between gap-3">
                <label className="text-xs text-slate-400">Data starts on row</label>
                <Input data-testid="header-row-input" type="number" min={1} value={headerRow} className="w-20 h-8 font-mono bg-[#0B0F17]"
                  onChange={(e) => { const v = Math.max(1, Number(e.target.value) || 1); setHeaderRow(v); if (file) loadPreview(file, v, true); }} />
              </div>
              {FIELDS.map((f) => (
                <div key={f.key}>
                  <div className="text-xs text-slate-400 mb-1.5">{f.label}{f.required && <span className="text-rose-400"> *</span>}</div>
                  <ColumnPicker columns={preview?.columns || []} value={mapping[f.key]} multi={f.multi} disabled={!preview}
                    testId={`map-${f.key}-select`} onChange={(v) => setMapping((m) => ({ ...m, [f.key]: v }))} />
                </div>
              ))}
              <div className="flex gap-2 pt-2">
                <Input data-testid="preset-name-input" placeholder="Save as (e.g. TSYS format)" value={presetName} onChange={(e) => setPresetName(e.target.value)} className="h-9 bg-[#0B0F17]" />
                <Button data-testid="save-preset-button" variant="outline" size="sm" className="h-9" onClick={savePreset} disabled={!preview}><Save size={14} /></Button>
              </div>
            </div>
          </Panel>

          <Panel title="2 · Import">
            <div className="p-5 space-y-4">
              <div>
                <div className="text-xs text-slate-400 mb-1.5">Report month</div>
                <Input data-testid="report-month-input" type="month" value={month} onChange={(e) => setMonth(e.target.value)} className="bg-[#0B0F17] font-mono" />
              </div>
              <div>
                <div className="text-xs text-slate-400 mb-1.5">Source / processor (optional)</div>
                <Input data-testid="report-source-input" value={source} onChange={(e) => setSource(e.target.value)} placeholder="e.g. TSYS" className="bg-[#0B0F17]" />
              </div>
              {missing.length > 0 && preview && <div className="text-xs text-amber-400">Map: {missing.map((m) => m.label).join(", ")}</div>}
              {preview?.latest_month && month && month < preview.latest_month && (
                <div data-testid="older-month-warning" className="text-xs text-amber-300 rounded border border-amber-500/30 bg-amber-500/10 p-2">
                  {monthLabel(month)} is earlier than your latest upload ({monthLabel(preview.latest_month)}). Any negative balance carried forward will change agent payouts in later months, including months already marked paid.
                </div>)}
              <Button data-testid="process-ingestion-button" disabled={!preview || missing.length > 0 || !month || busy} onClick={() => ingest()} className="w-full bg-emerald-500 hover:bg-emerald-600 text-slate-950 font-semibold">
                {busy ? "Calculating…" : "Import & calculate residuals"}
              </Button>
            </div>
          </Panel>

          {result && (
            <Panel>
              <div className="p-5 space-y-3" data-testid="ingest-result">
                <div className="flex items-center gap-2 text-emerald-400"><CheckCircle2 size={16} /> Imported {result.row_count} rows</div>
                <div className="font-mono text-sm">Residual total: {money(result.total_residual)}</div>
                <div className="font-mono text-sm text-emerald-400">Agent payouts: {money(result.total_payout)}</div>
                <div className="font-mono text-xs text-slate-400">{result.house_rows} house-account rows (no split)</div>
                {result.unmatched_agents.length > 0 && (
                  <div className="text-xs text-amber-300" data-testid="ingest-unmatched">
                    Unmatched agents: {result.unmatched_agents.map((u) => `${u.agent} (${u.rows})`).join(", ")}
                  </div>
                )}
                <Button data-testid="view-dashboard-button" variant="outline" className="w-full" onClick={() => nav("/admin")}>View dashboard</Button>
              </div>
            </Panel>
          )}
        </div>
      </div>
      {result && <PendingBrands month={result.month} className="mt-6" />}
      {result && <PendingFees month={result.month} className="mt-6" />}
      <BulkUpload />
    </div>
  );
}

function PreviewTable({ preview, mapping }) {
  const byLetter = Object.fromEntries(preview.columns.map((c) => [c.letter, c]));
  const cols = FIELDS.flatMap((f) => [].concat(mapping[f.key] || []).map((l) => ({ l, f })) ).filter((x) => byLetter[x.l]);
  return (
    <Panel title="Mapped columns preview" action={<span className="text-xs text-slate-500 font-mono">first {preview.sample_rows.length} of {preview.row_count} rows · {preview.columns.length} columns in sheet</span>}>
      {cols.length === 0 ? <div className="p-8 text-sm text-slate-500 text-center">Map columns on the right to preview them here.</div> : (
        <div className="overflow-x-auto" data-testid="sheet-preview-table">
          <table className="tbl w-full whitespace-nowrap">
            <thead><tr>{cols.map(({ l, f }) => (
              <th key={`${f.key}-${l}`} className="!text-blue-300 bg-blue-500/5">
                {l} · {byLetter[l].label}<div className="text-[10px] text-emerald-400 normal-case mt-0.5">→ {f.label}</div>
              </th>))}</tr></thead>
            <tbody>{preview.sample_rows.map((r, i) => (
              <tr key={`sheet-row-${preview.data_start_row + i}`}>{cols.map(({ l, f }) => <td key={`${f.key}-${l}`} className="font-mono text-xs text-slate-200">{r[l]}</td>)}</tr>
            ))}</tbody>
          </table>
        </div>
      )}
    </Panel>
  );
}
